import { useState, useEffect, useRef } from "react";
import { CheckCircle2, XCircle, Eye, FileText, History, Pencil, Trash2 } from "lucide-react";
import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useToast } from "../context/ToastContext.js";
import { useConfirm } from "./ui/confirm-dialog.js";
import { Button } from "./ui/button.js";
import { Badge } from "./ui/badge.js";
import { Tooltip } from "./ui/tooltip.js";
import { cn } from "../lib/utils.js";
import type { LightboxTarget } from "./DocumentLightbox.js";

export interface DocVersion {
  id: string;
  version: number;
  uploadedAt: string;
  extractedData: Record<string, unknown> | null;
  hrVerifiedAt: string | null;
  hrVerificationNotes: string | null;
  hrRejectedAt: string | null;
  hrRejectionReason: string | null;
}

export function DocVersionHistory({
  employeeId, docType, isHR, refreshKey, onVerify, onReplace, onChanged, title,
}: {
  /** The uploaded file's name, shown on the document card. */
  title?: string | undefined;
  employeeId: string; docType: string; isHR: boolean; refreshKey: number;
  onVerify: (t: LightboxTarget) => void;
  /** Re-upload through the slot's own handler; omit to hide Replace (locked / view-only). */
  onReplace?: ((f: File) => void) | undefined;
  onChanged?: (() => void) | undefined;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [docId, setDocId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const replaceInput = useRef<HTMLInputElement>(null);
  const [mimeType, setMimeType] = useState<string | null>(null);
  const [versions, setVersions] = useState<DocVersion[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data: docs } = await api.get<Array<{ id: string }>>(`/employees/${employeeId}/documents?type=${docType}`);
        const id = (docs as Array<{ id: string }>)[0]?.id;
        if (!id) { if (!cancelled) { setDocId(null); setVersions([]); } return; }
        if (cancelled) return;
        const { data } = await api.get<{ mimeType: string | null; versions: DocVersion[] }>(`/documents/${id}/versions`);
        if (!cancelled) { setDocId(id); setMimeType(data.mimeType); setVersions(data.versions); }
      } catch (err) { logError("DocVersionHistory.load", err); }
    })();
    return () => { cancelled = true; };
  }, [employeeId, docType, refreshKey]);

  async function del() {
    if (!docId) return;
    const ok = await confirm({
      title: "Delete this document?",
      description: "All versions of this document are removed from the profile.",
      actionText: "Delete",
      tone: "destructive",
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await api.delete(`/documents/${docId}`);
      setVersions([]); setDocId(null);
      onChanged?.();
    } catch (err) {
      logError("DocVersionHistory.delete", err);
      toast.show("Failed to delete document", "error");
    } finally {
      setDeleting(false);
    }
  }

  if (!versions.length) return null;

  const latest = versions[0]!;
  const target = (v: DocVersion): LightboxTarget => ({
    versionId: v.id, docType, version: v.version, mimeType, extractedData: v.extractedData,
    currentStatus: v.hrVerifiedAt ? "VERIFIED" : v.hrRejectedAt ? "REJECTED" : "UNVERIFIED", isHR,
  });
  const state = latest.hrVerifiedAt ? "verified" : latest.hrRejectedAt ? "rejected" : "pending";
  const statusText = { verified: "Verified by HR", rejected: "Rejected by HR", pending: "Pending HR review" }[state];
  const uploaded = new Date(latest.uploadedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const iconBtn = "size-7 p-0";

  return (
    <div className="min-w-0">
      {/* Same footprint as the empty dropzone (36px, full width): the file sits inside it. */}
      <div className={cn(
        "flex h-9 min-w-0 items-center gap-2 rounded-md border bg-surface-50 pl-3 pr-1.5 text-[13px]",
        state === "verified" ? "border-[var(--color-success)]/50" : state === "rejected" ? "border-[var(--color-error)]/50" : "border-surface-300",
      )}>
        <Tooltip
          side="top" align="start" maxWidth={260}
          content={
            <div className="space-y-0.5">
              <p className="break-all font-semibold">{title ?? "Uploaded document"}</p>
              <p>Uploaded {uploaded} · v{latest.version}</p>
              <p className={state === "verified" ? "text-[var(--color-success-fg)]" : state === "rejected" ? "text-[var(--color-error-fg)]" : ""}>{statusText}</p>
              {latest.hrRejectionReason && <p>Reason: {latest.hrRejectionReason}</p>}
            </div>
          }
        >
          <span className="flex min-w-0 flex-1 cursor-default items-center gap-2">
            {state === "verified" ? <CheckCircle2 size={15} className="shrink-0 text-success" />
              : state === "rejected" ? <XCircle size={15} className="shrink-0 text-error" />
              : <FileText size={15} className="shrink-0 text-surface-500" />}
            <span className="min-w-0 truncate font-medium text-surface-800">{title ?? "Uploaded document"}</span>
          </span>
        </Tooltip>
        <div className="flex shrink-0 items-center">
          <Tooltip content={isHR ? "Review" : "View"} side="top">
            <Button size="icon" variant="ghost" className={iconBtn} aria-label={isHR ? "Review document" : "View document"} onClick={() => onVerify(target(latest))}>
              <Eye size={14} />
            </Button>
          </Tooltip>
          {onReplace && (
            <>
              <input ref={replaceInput} type="file" accept="image/*,.pdf,.doc,.docx" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onReplace(f); e.target.value = ""; }} />
              <Tooltip content="Replace file" side="top">
                <Button size="icon" variant="ghost" className={iconBtn} aria-label="Replace with a new file" onClick={() => replaceInput.current?.click()}>
                  <Pencil size={14} />
                </Button>
              </Tooltip>
            </>
          )}
          {versions.length > 1 && (
            <Tooltip content={showHistory ? "Hide earlier versions" : `Earlier versions (${versions.length - 1})`} side="top">
              <Button size="icon" variant="ghost" className={cn(iconBtn, showHistory && "bg-surface-100")} aria-label="Earlier versions" aria-pressed={showHistory}
                onClick={() => setShowHistory((v) => !v)}>
                <History size={14} />
              </Button>
            </Tooltip>
          )}
          {isHR && (
            <Tooltip content="Delete document" side="top">
              <Button size="icon" variant="ghost" className={cn(iconBtn, "text-error hover:text-error")} aria-label="Delete document" disabled={deleting} onClick={() => void del()}>
                <Trash2 size={14} />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
      {showHistory && versions.length > 1 && (
        <div className="mt-1.5">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-surface-400">Earlier versions</p>
      {versions.slice(1).map((v) => {
        const verified = !!v.hrVerifiedAt;
        const rejected = !!v.hrRejectedAt;
        return (
          <div key={v.id} style={{
            display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px",
            padding: "6px 10px", borderRadius: "var(--radius-sm)",
            background: "var(--color-surface-50)", border: "1px solid var(--color-surface-100)",
            marginBottom: "4px", fontSize: "12px",
          }}>
            <span style={{ fontFamily: "var(--font-mono)", color: "var(--color-surface-500)", minWidth: "24px" }}>v{v.version}</span>
            <span style={{ color: "var(--color-surface-600)", flex: "1 1 auto", minWidth: 0 }}>
              {new Date(v.uploadedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            </span>
            {verified ? <Badge tone="success"><CheckCircle2 size={11} /> Verified</Badge>
              : rejected ? <Badge tone="error"><XCircle size={11} /> Rejected</Badge>
              : <Badge tone="neutral">Pending</Badge>}
            <Button size="icon" variant="ghost" title={isHR ? "Review" : "View"} aria-label={isHR ? "Review version" : "View version"} onClick={() => onVerify(target(v))}>
              <Eye size={13} />
            </Button>
          </div>
        );
      })}
        </div>
      )}
    </div>
  );
}
